import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { CarregandoBloco, CarregandoCards } from "@/components/carregando";
import FiltroPeriodo from "@/features/relatorio-vendas/components/FiltroPeriodo";
import { Esmaecer } from "@/features/relatorio-vendas/components/Esqueletos";
import { useDados, usePeriodo } from "@/features/relatorio-vendas/lib/usePeriodo";

import ComandasAba from "./abas/ComandasAba";
import EntregasAba from "./abas/EntregasAba";
import MesasAba from "./abas/MesasAba";
import ResumoAba from "./abas/ResumoAba";
import { buscarComandas, buscarEntregas, buscarMesas, buscarResumo } from "./api";
import { NotaComparacao } from "./componentes/Base";
import IconeModulo from "./componentes/IconeModulo";
import { ABAS } from "./modulos";

// Busca e tela de cada aba. Fora do componente: a função precisa ser a mesma a
// cada render, senão o useDados buscaria sem parar.
const CONTEUDO = {
  resumo: { buscar: buscarResumo, Aba: ResumoAba },
  mesas: { buscar: buscarMesas, Aba: MesasAba },
  comandas: { buscar: buscarComandas, Aba: ComandasAba },
  delivery: { buscar: buscarEntregas("delivery"), Aba: EntregasAba },
  retirada: { buscar: buscarEntregas("retirada"), Aba: EntregasAba },
  ifood: { buscar: buscarEntregas("ifood"), Aba: EntregasAba },
};

function Esqueleto() {
  return (
    <div className="space-y-6" data-e2e="esqueleto-tempo">
      <CarregandoCards quantos={4} className="grid grid-cols-2 lg:grid-cols-4 gap-3" altura="h-[108px]" />
      <CarregandoBloco altura="h-[320px]" />
      <CarregandoBloco altura="h-[280px]" />
    </div>
  );
}

function ConteudoAba({ aba, periodo, definirAtualizar, aoAbrirAba }) {
  const { buscar, Aba } = CONTEUDO[aba];
  const { dados, carregando, carregou, recarregar } = useDados(buscar, periodo, { rotulo: "o relatório de tempo" });
  useEffect(() => { definirAtualizar({ recarregar, carregando }); }, [definirAtualizar, recarregar, carregando]);
  if (!carregou) return <Esqueleto />;
  if (!dados) return null;
  return (
    <Esmaecer ativo={carregando}>
      <NotaComparacao periodo={dados.anterior?.periodo} />
      <Aba dados={dados} canal={aba} periodo={periodo} aoAbrirAba={aoAbrirAba} />
    </Esmaecer>
  );
}

// Relatórios → Relatório de Tempo (pedido do dono, 27/10/2026): quanto tempo
// cada venda leva e quanto fatura nesse tempo, módulo a módulo. Um período só
// para todas as abas (trocar de aba mantém as datas).
export default function RelatorioTempoPage() {
  const [params, setParams] = useSearchParams();
  const aba = CONTEUDO[params.get("aba")] ? params.get("aba") : "resumo";
  const p = usePeriodo();
  const [acao, setAcao] = useState({ recarregar: null, carregando: false });
  const definirAtualizar = useCallback((a) => setAcao(a), []);
  const abrirAba = useCallback((key) => {
    setParams((atual) => { const n = new URLSearchParams(atual); n.set("aba", key); return n; }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [setParams]);

  return (
    <div className="p-4 sm:p-6 space-y-5" data-e2e="relatorio-tempo">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Relatório de Tempo</h1>
          <p className="text-muted-foreground mt-1">Quanto tempo cada venda leva e quanto ela fatura nesse tempo, módulo a módulo</p>
        </div>
        <Button variant="outline" onClick={() => acao.recarregar?.()} disabled={acao.carregando || !acao.recarregar} className="gap-2 self-start md:self-auto">
          <RefreshCw className={`w-4 h-4 ${acao.carregando ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      <FiltroPeriodo {...p} />

      <nav className="flex gap-1.5 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0" style={{ scrollbarWidth: "none" }} aria-label="Módulos" data-e2e="abas-tempo">
        {ABAS.map((a) => {
          const ativa = a.key === aba;
          return (
            <button key={a.key} type="button" onClick={() => abrirAba(a.key)} aria-current={ativa ? "page" : undefined}
              data-e2e={`aba-${a.key}`}
              className={`shrink-0 inline-flex items-center gap-2 h-10 pl-1.5 pr-3.5 rounded-xl border text-sm font-semibold transition-colors ${
                ativa ? "bg-primary text-primary-foreground border-primary shadow-sm" : "bg-card hover:border-primary/40"}`}>
              {ativa
                ? <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-primary-foreground/20">
                  {a.logo ? <img src={a.logo} alt="" className="w-4 h-4 object-contain" /> : <a.Icon className="w-4 h-4" />}
                </span>
                : <IconeModulo modulo={a} tamanho="w-7 h-7" />}
              {a.nome}
            </button>
          );
        })}
      </nav>

      <ConteudoAba key={aba} aba={aba} periodo={p.periodo} definirAtualizar={definirAtualizar} aoAbrirAba={abrirAba} />
    </div>
  );
}
